import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import AppWindow from './AppWindow';
import { TOP_BAR_HEIGHT } from './Desktop/TopBar';

const DOCK_HEIGHT = 80;

function AppWindowLayer({ apps, openApps, onCloseApp }, ref) {
  const wrapperRef = useRef(null);
  const [windows, setWindows] = useState([]);
  const zCounterRef = useRef(100);

  /* ------------------------------------------------------------------ */
  /* helpers                                                            */
  /* ------------------------------------------------------------------ */
  const minX = 2;
  const minY = 2;
  const getMaxW = (desktopW) => Math.floor(desktopW) - 2 * minX;
  const getMaxH = (desktopH) => Math.floor(desktopH) - DOCK_HEIGHT - 2 * minY;
  const rectsOverlap = (r1, r2) =>
    !(
      r1.x + r1.w <= r2.x ||
      r2.x + r2.w <= r1.x ||
      r1.y + r1.h <= r2.y ||
      r2.y + r2.h <= r1.y
    );

  /** Find the first free top-left slot for a window of size (w,h) */
  function getNextFreePosition(w, h, desktopW, desktopH, existing) {
    /* assume we have room for full padding */
    const padding = 32;
    const step = 32;

    /* scan rows then columns */
    for (let y = padding; y + h <= desktopH - DOCK_HEIGHT; y += step + padding) {
      for (let x = padding; x + w <= desktopW; x += step + padding) {
        const probe = { x, y, w, h };
        const clash = existing.some(({ x: x2, y: y2, width: w2, height: h2 }) =>
          rectsOverlap(probe, { x: x2, y: y2, w: w2, h: h2 })
        );
        if (!clash) return { x, y };
      }
    }

    /* if the screen has room for the window + padding, cascade from last */
    const maxW = getMaxW(desktopW);
    const maxH = getMaxH(desktopH);
    if (w <= (maxW - 2 * padding) && h <= (maxH - 2 * padding)) {
      const last = existing[existing.length - 1];
      return last
        ? {
          x: (last.x + padding) % (maxW - w),
          y: (last.y + padding) % (maxH - h)
        }
        : { x: padding, y: padding };
    }

    /* There's no room at the inn. Layout for max dimensions */
    return {
      x: minX, y: minY
    }
  }

  /** Create (or ignore if exists) a new window for appId */
  function openApp(appId) {
    const app = apps.find(a => a.id === appId);
    if (!app) return;

    const desktopW = wrapperRef.current?.offsetWidth || window.innerWidth;
    const desktopH = wrapperRef.current?.offsetHeight || window.innerHeight;
    const maxW = getMaxW(desktopW);
    const maxH = getMaxH(desktopH);
    const width = Math.min(app.defaultWidth || 400, maxW);
    const height = Math.min(app.defaultHeight || 300, maxH);

    // Pre-increment outside the state updater so the updater stays pure
    const z = ++zCounterRef.current;

    setWindows(prev => {
      /* already open? – do nothing */
      if (prev.some(w => w.id === appId)) return prev;

      // Use default positions if provided, otherwise find next free position
      let x, y;
      if (app.defaultX !== undefined || app.defaultY !== undefined) {
        const autoPos = getNextFreePosition(width, height, desktopW, desktopH, prev);
        x = app.defaultX !== undefined ? app.defaultX : autoPos.x;
        y = app.defaultY !== undefined ? app.defaultY : autoPos.y;
      } else {
        const pos = getNextFreePosition(width, height, desktopW, desktopH, prev);
        x = pos.x;
        y = pos.y;
      }

      return [...prev, { id: appId, x, y, width, height, z }];
    });
  }

  /** Promote the clicked window to front */
  const handleFocus = useCallback(id => {
    setWindows(prev => {
      const topZ = Math.max(...prev.map(w => w.z), zCounterRef.current);
      const newZ = topZ + 1;
      return prev.map(w => w.id === id ? { ...w, z: newZ } : w);
    });
    zCounterRef.current++;
  }, []);

  useImperativeHandle(ref, () => ({ focus: handleFocus }), [handleFocus]);

  /* ------------------------------------------------------------------ */
  /* sync window list with openApps prop                                */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    /* open any brand-new apps */
    openApps.forEach(openApp);

    /* close apps that were removed */
    setWindows(prev => prev.filter(w => openApps.includes(w.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openApps]); // intentionally NOT depending on windows

  /* ------------------------------------------------------------------ */
  /* render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      {windows.map(win => {
        const app = apps.find(a => a.id === win.id);
        if (!app) return null;

        return (
          <AppWindow
            key={win.id}
            id={win.id}
            title={app.title}
            zIndex={win.z}
            onClose={onCloseApp}
            onFocus={handleFocus}
            style={app.style}
            initialX={win.x}
            initialY={win.y}
            initialWidth={win.width}
            initialHeight={win.height}
          >
            {app.content}
          </AppWindow>
        );
      })}
    </div>
  );
}

export default React.memo(React.forwardRef(AppWindowLayer));
